import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCopy, FaKey, FaPlus } from 'react-icons/fa';
import { copyText, del, fail, formatWhen, get, post, publicBase } from './api';

export const CodeBlock = ({ code, label }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    return (
        <div className="co-code">
            <div className="co-code-head">
                <span>{label}</span>
                <button type="button" className="co-link" onClick={async () => { setCopied(await copyText(code)); setTimeout(() => setCopied(false), 1500); }}>
                    <FaCopy aria-hidden="true" /> {copied ? t('commerceOrders.copied', 'Copié') : t('commerceOrders.copy', 'Copier')}
                </button>
            </div>
            <pre><code>{code}</code></pre>
        </div>
    );
};

export const SecretReveal = ({ title, value, hint, onDone }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    return (
        <div className="co-reveal" role="status">
            <strong>{title}</strong>
            <div className="co-reveal-value">
                <code>{value}</code>
                <button type="button" className="u-btn u-btn--secondary" onClick={async () => setCopied(await copyText(value))}>
                    <FaCopy aria-hidden="true" /> {copied ? t('commerceOrders.copied', 'Copié') : t('commerceOrders.copy', 'Copier')}
                </button>
            </div>
            <p>{hint}</p>
            <button type="button" className="co-link" onClick={onDone}>{t('commerceOrders.secretSaved', 'Je l’ai enregistré')}</button>
        </div>
    );
};

const pushExample = (base) => `curl -X POST ${base}/orders/batch \\
  -H "X-API-Key: uo_…" -H "Content-Type: application/json" \\
  -d '{
  "orders": [{
    "external_id": "SO-1001",
    "number": "#1001",
    "status": "shipped",
    "client_ref": "MA-MARQUE",
    "placed_at": "2026-10-05T09:12:00Z",
    "currency": "EUR",
    "total": 49.90,
    "customer": {"name": "Ada Lovelace", "email": "ada@example.com", "phone": "+33600000000"},
    "shipping_address": {"street": "1 rue de la Paix", "postcode": "75002", "city": "Paris", "country": "FR"},
    "ship_from": {"company": "Entrepôt Sud", "postcode": "13127", "city": "Vitrolles", "country": "FR"},
    "items": [{"sku": "MUG-01", "name": "Mug", "quantity": 2, "unit_price": 12.50}],
    "shipments": [{
      "tracking_number": "6A12345678901",
      "carrier": "Colissimo",
      "service": "Expert F",
      "shipped_at": "2026-10-05T16:40:00Z",
      "insurance": false,
      "weight_kg": 1.2,
      "length_cm": 30, "width_cm": 20, "height_cm": 10,
      "content": "Mugs en céramique",
      "product_category": "maison",
      "delivery_mode": "relay",
      "relay_point": {"id": "FR-012345", "name": "Tabac de la Gare", "postcode": "75012", "city": "Paris", "country": "FR"}
    }]
  }]
}'`;

const shipmentExample = (base) => `curl -X POST "${base}/orders/4501/shipments?source=shopify" \\
  -H "X-API-Key: uo_…" -H "Content-Type: application/json" \\
  -d '{"tracking_number": "6A12345678901", "carrier": "Colissimo", "notify_customer": true}'`;

const pullExample = (base, t) => `# ${t('commerceOrders.pullFirst', '1er appel : depuis une date')}
curl "${base}/orders?updated_since=2026-10-01T00:00:00Z&limit=500" -H "X-API-Key: uo_…"
# ${t('commerceOrders.pullNext', 'Appels suivants : reprenez au curseur renvoyé')}
curl "${base}/orders?cursor=<next_cursor>&limit=500" -H "X-API-Key: uo_…"
# ${t('commerceOrders.pullProblems', 'Seulement les commandes dont un colis a un problème en cours')}
curl "${base}/orders?problem=true" -H "X-API-Key: uo_…"`;

const ApiTab = ({ isAdmin, monitoring }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [keys, setKeys] = useState(null);
    const [name, setName] = useState('');
    const [created, setCreated] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [confirmId, setConfirmId] = useState(null);
    const base = publicBase();

    const load = useCallback(async () => {
        try {
            const res = await get('/api-keys');
            setKeys(res.data.items || []);
        } catch (err) {
            setError(fail(err, t('commerceOrders.keysError', 'Impossible de charger les clés.')));
            setKeys([]);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError('');
        try {
            const res = await post('/api-keys', { name: name.trim() });
            setCreated(res.data);
            setName('');
            load();
        } catch (err) {
            setError(fail(err, t('commerceOrders.keyCreateError', 'Impossible de créer la clé.')));
        } finally {
            setBusy(false);
        }
    };

    const revoke = async (id) => {
        setError('');
        try {
            await del(`/api-keys/${id}`);
            setConfirmId(null);
            load();
        } catch (err) {
            setError(fail(err, t('commerceOrders.keyRevokeError', 'Impossible de révoquer la clé.')));
        }
    };

    return (
        <div className="co-stack">
            <section className="co-card co-pad">
                <header className="co-section-head">
                    <FaKey aria-hidden="true" />
                    <div>
                        <h3>{t('commerceOrders.keysTitle', 'Clés API')}</h3>
                        <p className="co-muted">{t('commerceOrders.keysIntro', 'Une clé par système qui envoie ou lit vos commandes (ERP, WMS, OMS). Elle ne donne accès qu’aux commandes de ce compte.')}</p>
                    </div>
                </header>
                {error && <div className="co-alert co-alert--error" role="alert">{error}</div>}
                {created && (
                    <SecretReveal title={t('commerceOrders.keyCreated', 'Clé « {{name}} » créée', { name: created.name })} value={created.api_key}
                        hint={t('commerceOrders.keyOnce', 'Copiez-la maintenant : elle ne sera plus jamais affichée. En cas de perte, révoquez-la et créez-en une autre.')}
                        onDone={() => setCreated(null)} />
                )}
                {keys && keys.length > 0 && (
                    <table className="co-table co-table--compact">
                        <thead>
                            <tr>
                                <th>{t('commerceOrders.keyName', 'Nom')}</th>
                                <th>{t('commerceOrders.keyPrefix', 'Clé')}</th>
                                <th>{t('commerceOrders.keyCreatedAt', 'Créée')}</th>
                                <th>{t('commerceOrders.keyLastUsed', 'Dernier usage')}</th>
                                <th aria-hidden="true" />
                            </tr>
                        </thead>
                        <tbody>
                            {keys.map((k) => (
                                <tr key={k.id}>
                                    <td>{k.name}</td>
                                    <td className="co-mono">{k.key_prefix}…</td>
                                    <td>{formatWhen(k.created_at, lang)}</td>
                                    <td>{k.last_used_at ? formatWhen(k.last_used_at, lang) : t('commerceOrders.never', 'jamais')}</td>
                                    <td className="co-num">
                                        {isAdmin && (confirmId === k.id ? (
                                            <span className="co-confirm">
                                                <button type="button" className="co-link co-link--danger" onClick={() => revoke(k.id)}>{t('commerceOrders.confirmRevoke', 'Révoquer définitivement')}</button>
                                                <button type="button" className="co-link" onClick={() => setConfirmId(null)}>{t('commerceOrders.cancel', 'Annuler')}</button>
                                            </span>
                                        ) : (
                                            <button type="button" className="co-link co-link--danger" onClick={() => setConfirmId(k.id)}>{t('commerceOrders.revoke', 'Révoquer')}</button>
                                        ))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {keys && keys.length === 0 && <p className="co-muted">{t('commerceOrders.noKey', 'Aucune clé pour l’instant.')}</p>}
                {isAdmin ? (
                    <form className="co-inline-form" onSubmit={create}>
                        <input type="text" maxLength={64} value={name} onChange={(e) => setName(e.target.value)}
                            placeholder={t('commerceOrders.keyNamePlaceholder', 'Nom de la clé (ex. ERP production)')} />
                        <button type="submit" className="u-btn u-btn--primary" disabled={busy || !name.trim()}>
                            <FaPlus aria-hidden="true" /> {t('commerceOrders.createKey', 'Créer une clé')}
                        </button>
                    </form>
                ) : <p className="co-muted co-small">{t('commerceOrders.adminOnlyKeys', 'Seul un administrateur peut créer ou révoquer des clés.')}</p>}
            </section>

            <section className="co-card co-pad co-docs">
                <h3>{t('commerceOrders.docsTitle', 'Envoyer et lire vos commandes')}</h3>
                <p>{t('commerceOrders.docsIntro', 'Envoyez vos commandes par lots (jusqu’à 1 000 par appel). L’envoi est idempotent sur external_id : renvoyer une commande la met à jour, sans doublon.')}</p>
                <ul className="co-facts">
                    <li><strong>{t('commerceOrders.docsBase', 'Adresse')}</strong> <code>{base}</code></li>
                    <li><strong>{t('commerceOrders.docsAuth', 'Authentification')}</strong> <code>X-API-Key: uo_…</code></li>
                    <li><strong>{t('commerceOrders.docsLimits', 'Limites')}</strong> {t('commerceOrders.docsLimitsValue', '1 000 commandes par envoi · 500 commandes par page en lecture · 120 appels par minute et par clé')}</li>
                    <li><strong>{t('commerceOrders.docsStatuses', 'Statuts')}</strong> <code>pending preparing shipped delivered incident returned cancelled</code></li>
                </ul>
                <div className={`co-alert ${monitoring ? 'co-alert--success' : 'co-alert--info'}`}>
                    {monitoring
                        ? t('commerceOrders.docsMonitoringOn', 'Chaque numéro de suivi envoyé est surveillé par la surveillance colis : un problème vous est signalé sur cette page, par webhook et sur la commande Shopify / PrestaShop.')
                        : t('commerceOrders.docsMonitoringOff', 'La surveillance colis est désactivée : les commandes sont enregistrées mais leurs colis ne sont pas suivis. Activez-la dans Surveillance colis.')}
                </div>
                <CodeBlock label={t('commerceOrders.docsPush', 'Envoyer des commandes — POST /orders/batch')} code={pushExample(base)} />
                <p className="co-muted co-small">{t('commerceOrders.docsParcelFields', 'Les modèles de risque lisent le transporteur, le client (client_ref), les codes postaux d’expédition (ship_from) et de livraison, l’assurance, la valeur de la commande et les dates : renseignez-les. Poids, dimensions, contenu, catégorie, mode de livraison et point relais servent au prochain entraînement des modèles.')}</p>
                <p className="co-muted co-small">{t('commerceOrders.docsPushAnswer', 'Réponse : created, updated, errors (index + raison pour chaque commande refusée, les autres sont enregistrées), parcels_registered.')}</p>
                <CodeBlock label={t('commerceOrders.docsPull', 'Lire les commandes — GET /orders (curseur)')} code={pullExample(base, t)} />
                <p className="co-muted co-small">{t('commerceOrders.docsPullAnswer', 'Réponse : data (commandes avec l’état de chaque colis et les problèmes en cours), next_cursor, has_more. Une commande revient dès qu’elle change. Une seule commande : GET /orders/{external_id}?source=api|shopify|prestashop.')}</p>
                <CodeBlock label={t('commerceOrders.docsShipment', 'Ajouter un colis expédié — POST /orders/{external_id}/shipments')} code={shipmentExample(base)} />
                <p className="co-muted co-small">{t('commerceOrders.docsShipmentAnswer', 'Votre WMS a expédié la commande : le colis est ajouté et surveillé. Pour une commande Shopify ou PrestaShop dont la boutique a « numéros de suivi » activé, le numéro est inscrit sur la commande de la boutique (shop_push : queued). Plusieurs boutiques d’une même plateforme : ajoutez shop=<identifiant>.')}</p>
            </section>
        </div>
    );
};

export default ApiTab;
